import {
    BrowserModule,
    HAMMER_GESTURE_CONFIG,
    HammerGestureConfig,
    HammerModule,
} from "@angular/platform-browser"
import { NgModule, ɵɵtemplateRefExtractor } from "@angular/core"
import { RouterModule, Routes } from "@angular/router"
import { FormsModule, ReactiveFormsModule } from "@angular/forms"
import { HttpClientModule } from "@angular/common/http"
import { RecaptchaModule } from "ng-recaptcha"
import { Injectable } from "@angular/core"
import * as Hammer from "hammerjs"
import { MatListModule } from "@angular/material/list"
import { SimplebarAngularModule } from "simplebar-angular"
import { AuthGuard, NegAuthGuard } from "./auth.gard"
import { PlayerService } from "./services/audio/player.service"
import { ModalService } from "./services/modal.service"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatFormFieldModule } from "@angular/material/form-field"
import { BrowserAnimationsModule } from "@angular/platform-browser/animations"
import { DragDropModule } from "@angular/cdk/drag-drop"
import { OverlayModule } from "@angular/cdk/overlay"
import { FileSaverModule } from "ngx-filesaver"
import { AnalyticsService } from "./services/analytics.service"
import { ApiService } from "./services/api.service"
import { PinchToZoomDirective } from "./directives/pinchtozoom/pinchtozoom.directive"
import { TooltipDirective } from "./directives/tooltip/tooltip.directive"
import { ModalDirective } from "./directives/modal/modal.directive"
import { TooltipComponent } from "./directives/tooltip/tooltip.component"
import { FolderService } from "./services/folder.service"
import { GeneralService } from "./services/general/general.service"
import { BillingService } from "./services/billing.service"
import { UserService } from "./services/user.service"
import { TokenService } from "./services/token.service"
import { LoginHandoffService } from "./services/login-handoff.service"
import { AppComponent } from "./components/app.component"
import { ProgressBarComponent } from "./components/reusable/progressbar/progressbar.component"
import { NavbarComponent } from "./components/navbar/navbar.component"
import { PlayerComponent } from "./components/player/player.component"
import { SongLyricsPanelComponent } from "./components/player/song-lyrics-panel/song-lyrics-panel.component"
import { LoginComponent } from "./components/login/login.component"
import { WaitingOnBrowserLoginComponent } from "./components/waitingonbrowserlogin/waitingonbrowserlogin.component"
import { NotFoundComponent } from "./components/notfound/notfound.component"
import { BillingComponent } from "./components/billing/billing.component"
import { MyTracksComponent } from "./components/mytracks/mytracks.component"
import { ModalsComponent } from "./components/modals/modals.component"
import { DropdownComponent } from "./components/reusable/dropdown/dropdown.component"
import { DropdownWindowComponent } from "./components/reusable/dropdown/dropdown-window/dropdown-window.component"
import { DialComponent } from "./components/reusable/dial/dial.component"
import { ResetComponent } from "./components/reset/reset.component"
import { StepsComponent } from "./components/reusable/steps/steps.component"
import { RadioButtonComponent } from "./components/reusable/radio-button/radio-button.component"
import { IntegerInputFieldComponent } from "./components/reusable/integer-input-field/integer-input-field.component"
import { TopMenuComponent } from "./components/reusable/topmenu/topmenu.component"
import { HTTP_INTERCEPTORS } from "@angular/common/http"
import { TokenInterceptor } from "./services/token.interceptor"

import { RoundProgressModule } from "angular-svg-round-progressbar"

import { PerfectScrollbarConfigInterface } from "ngx-perfect-scrollbar"

import { DeviceDetectorService } from "ngx-device-detector"

import { CreateAccountComponent } from "./components/createaccount/createaccount.component"
import { MessageService } from "./services/message.service"
import { SurveyService } from "./services/survey.service"
import { StreamingService } from "./services/audio/streaming.service"
import { HelperService } from "@helpers/helper.service"
import { DesignService } from "./services/design.service"
import { AnimationLoopService } from "./services/animationloop.service"
import { FilterPipe } from "@pipes/filter.pipe"
import { ExcludePipe } from "@pipes/exclude.pipe"
import { SlideToggleComponent } from "./components/reusable/slide-toggle/slide-toggle.component"
import { environment } from "../environments/environment"
import { EmotionSelectComponent } from "./components/reusable/emotion-select/emotion-select.component"
import { ButtonComponent } from "./components/reusable/button/button.component"
import { RangeComponent } from "./components/reusable/range/range.component"
import { AButtonDirective } from "./directives/a-button/a-button.directive"
import { CreateComponent } from "./components/create/create.component"
import { LyricsChatComponent } from "./components/create/lyrics-chat/lyrics-chat.component"
import { ProgressCircleComponent } from "./components/reusable/progress-circle/progress-circle.component"
import { NotificationsService } from "@services/notifications.service"
import { NotificationsComponent } from "./components/reusable/notifications/notifications.component"
import { AudioTrimmerComponent } from "./components/reusable/audio-trimmer/audio-trimmer.component"
import { AudioService } from "@services/audio/audio.service"
import { ListComponent } from "./components/reusable/list/list.component"
import { LocalContextMenuComponent } from "./components/reusable/context-menus/local-context-menu/local-context-menu.component"
import { IconSelectorComponent } from "./components/reusable/icon-selector/icon-selector.component"
import { LoadingStatusService } from "@services/loadingstatus.service"
import { SupportChatService } from "@services/supportchat.service"
import { WindowService } from "./services/window.service"
import { SimpleModalComponent } from "./components/modals/simple-modal/simple-modal.component"
import { MenuOptionsComponent } from "./components/reusable/menu-options/menu-options.component"
import { RenameComponent } from "./components/modals/rename/rename.component"
import { MenuModifiersComponent } from "./components/reusable/menu-modifiers/menu-modifiers.component"
import { PlayerHttpService } from "@services/audio/player.http.service"

import { enableAkitaProdMode } from "@datorama/akita"
import { ShortcutsService } from "@services/shortcuts.service"
import { SettingsModalComponent } from "./components/modals/settings/settings.component"
import { ChangelogModal } from "./components/modals/changelog/changelog.modal"
import { DoActionBeforeModal } from "./components/modals/do-action-before/do-action-before.modal"
import { ConfirmDiscardChangesService } from "@services/confirm-discard-changes.service"
import { TopMenuWithArrowsComponent } from "./components/reusable/topmenu-with-arrows/top-menu-with-arrows.component"
import { BtnSelectComponent } from "./components/reusable/btn-select/btn-select.component"
import { BigPlaybackButton } from "./components/reusable/big-playback-button/big-playback-button.component"
import { DropdownButtonComponent } from "./components/reusable/dropdown-button/dropdown-button.component"
import { SlideToggleWithTitlesComponent } from "./components/reusable/slide-toggle-with-titles/slide-toggle-with-titles.component"
import { ListItemsComponent } from "./components/reusable/list-items/list-items.component"
import { SearchComponent } from "./components/reusable/search/search.component"
import { PaginationComponent } from "./components/reusable/pagination/pagination.component"
import { ContextMenuComponent } from "./components/reusable/context-menu/context-menu.component"
import { MobileContextSheetDragDirective } from "./components/reusable/mobile-context-sheet/mobile-context-sheet-drag.directive"
import { MagicButtonComponent } from "./components/reusable/magic-button/magic-button.component"
import { SuccessModalCopmonent } from "./components/modals/success-modal/success-modal.component"
import { MagicPromptComponent } from "./components/reusable/magic-prompt/magic-prompt.component"
import { LikeBtnComponent } from "./components/reusable/like-btn/like-btn.component"
import { CommonModule } from "@angular/common"
import { ResizerComponent } from "./components/reusable/resizer/resizer.component"
import { TableComponent } from "./components/reusable/table/table.component"
import { ScrollingModule } from "@angular/cdk/scrolling"
import { TableTooltipComponent } from "./components/reusable/table/table-tooltip/table-tooltip.component"
import { NG_SCROLLBAR_OPTIONS, NgScrollbarModule } from "ngx-scrollbar"
import { TracksService } from "@services/tracks.service"
import { TracksHttpService } from "@services/tracks-http.service"

enableAkitaProdMode()

// const obstructed = require('obstructed')

if (!environment.production) {
    // const timer = obstructed(function(ms) {
    // 	// console.log('Main thread was blocked for about ' + ms + 'ms')
    // })
}

@Injectable()
export class HammerConfig extends HammerGestureConfig {
    overrides = <any>{
        swipe: { direction: Hammer.DIRECTION_ALL },
    }
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: false,
}

let appRoutes: Routes = [
    {
        path: "login",
        component: LoginComponent,
        canActivate: [NegAuthGuard],
        data: { title: "Login" },
    },

    {
        path: "reset/:token",
        component: ResetComponent,
        canActivate: [NegAuthGuard],
        data: { title: "Reset your password" },
    },

    {
        path: "signup/:signup",
        redirectTo: "login/:signup",
    },

    {
        path: "login/:signup",
        component: LoginComponent,
        canActivate: [NegAuthGuard],
        data: { title: "Login" },
    },

    {
        path: "create",
        component: CreateComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "createaccount",
        component: CreateAccountComponent,
        canActivate: [NegAuthGuard],
        data: { title: "Create account" },
    },

    {
        path: "waitingonbrowserlogin/:type",
        component: WaitingOnBrowserLoginComponent,
        canActivate: [NegAuthGuard],
    },
    {
        path: "",
        component: MyTracksComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "mytracks/:type",
        component: MyTracksComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "mytracks/:contentType/:folderID/:compositionID/:view",
        component: MyTracksComponent,
        canActivate: [AuthGuard],
    },

    {
        path: "billing",
        component: BillingComponent,
        canActivate: [AuthGuard],
    },
]

appRoutes.push({
    path: "**",
    redirectTo: "",
})

@NgModule({
    declarations: [
        AppComponent,
        BtnSelectComponent,
        NavbarComponent,
        PlayerComponent,
        SongLyricsPanelComponent,
        LoginComponent,
        TooltipComponent,
        TooltipDirective,
        ModalDirective,
        SettingsModalComponent,
        PinchToZoomDirective,
        NotFoundComponent,
        MyTracksComponent,
        ModalsComponent,
        SimpleModalComponent,
        ProgressBarComponent,
        MenuOptionsComponent,
        ResetComponent,
        BillingComponent,
        DropdownComponent,
        ContextMenuComponent,
        MobileContextSheetDragDirective,
        DropdownWindowComponent,
        StepsComponent,
        DoActionBeforeModal,
        AudioTrimmerComponent,
        DropdownButtonComponent,
        DialComponent,
        NotificationsComponent,
        CreateAccountComponent,
        WaitingOnBrowserLoginComponent,
        MagicPromptComponent,
        BigPlaybackButton,
        IntegerInputFieldComponent,
        SlideToggleWithTitlesComponent,
        SearchComponent,
        ListItemsComponent,
        RadioButtonComponent,
        SlideToggleComponent,
        FilterPipe,
        ExcludePipe,
        AButtonDirective,
        CreateComponent,
        LyricsChatComponent,
        TopMenuComponent,
        IconSelectorComponent,
        MenuModifiersComponent,
        EmotionSelectComponent,
        PaginationComponent,
        MagicButtonComponent,
        ButtonComponent,
        RangeComponent,
        ListComponent,
        RenameComponent,
        LocalContextMenuComponent,
        ProgressCircleComponent,
        ChangelogModal,
        TopMenuWithArrowsComponent,
        SuccessModalCopmonent,
        LikeBtnComponent,
        ResizerComponent,
        TableComponent,
        TableTooltipComponent,
    ],
    imports: [
        CommonModule,
        // PerfectScrollbarModule,
        ScrollingModule,
        FileSaverModule,
        OverlayModule,
        DragDropModule,
        SimplebarAngularModule,
        BrowserAnimationsModule,
        MatFormFieldModule,
        // ClickOutsideModule,
        MatListModule,
        FormsModule,
        BrowserModule,
        ReactiveFormsModule,
        RoundProgressModule,
        RecaptchaModule,
        MatInputModule,
        MatSelectModule,

        RouterModule.forRoot(
            appRoutes
            //{ enableTracing: true } // <-- debugging purposes only
        ),

        HttpClientModule,
        HammerModule,
        NgScrollbarModule,
    ],

    providers: [
        AuthGuard,
        NegAuthGuard,
        PlayerService,
        ModalService,
        ShortcutsService,
        GeneralService,
        ApiService,
        ConfirmDiscardChangesService,
        NotificationsService,
        FolderService,
        BillingService,
        UserService,
        HelperService,
        TokenService,
        LoginHandoffService,
        AnalyticsService,
        AnimationLoopService,
        LoadingStatusService,
        AudioService,
        PlayerHttpService,
        SupportChatService,
        MessageService,
        SurveyService,
        StreamingService,
        DesignService,
        DeviceDetectorService,
        WindowService,
        TracksService,
        TracksHttpService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },

        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: HammerConfig,
        },
        {
            provide: NG_SCROLLBAR_OPTIONS,
            useValue: {
                visibility: "always",
            },
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
